import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../api/auth';
import { ArrowRight } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const origin = window.location.origin;
  const defaultRedirect = origin.endsWith('/') ? origin : `${origin}/`;
  const rawRedirectUri = searchParams.get('redirect_uri');
  const isSafeRedirectUri = (value: string | null) => {
    if (!value) return false;
    try {
      const parsed = new URL(value, window.location.origin);
      return !parsed.pathname.startsWith('/login') && !parsed.pathname.startsWith('/signup');
    } catch {
      return false;
    }
  };
  const redirectUri = isSafeRedirectUri(rawRedirectUri) ? rawRedirectUri! : defaultRedirect;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authApi.login({ email, password, redirectUri });
      const { isSuccess, result, message } = response.data;

      if (isSuccess && result) {
        localStorage.setItem('accessToken', result.accessToken);
        window.location.replace(result.redirectUri || defaultRedirect);
      } else {
        setError(message || '로그인에 실패했습니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '서버 통신 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-orbit auth-orbit-a" aria-hidden="true" />
      <div className="auth-orbit auth-orbit-b" aria-hidden="true" />
      <div className="auth-orbit auth-orbit-c" aria-hidden="true" />

      <div className="auth-shell auth-shell-login-only fade-in">
        <div className="auth-brand-corner" aria-hidden="true">
          <div className="auth-brand-wordmark">omongkr</div>
        </div>
        <section className="auth-panel card">
          <div className="auth-panel-head">
            <h2 className="auth-panel-title">통합로그인</h2>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label className="label">이메일 주소</label>
              <input
                type="email"
                className="input auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="name@company.com"
              />
            </div>

            <div className="auth-field">
              <label className="label">비밀번호</label>
              <input
                type="password"
                className="input auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            {error && <div className="auth-error-box">{error}</div>}

            <button type="submit" className="btn btn-primary auth-submit-btn" disabled={isLoading}>
              {isLoading ? '인증 중...' : <>로그인 <ArrowRight size={18} /></>}
            </button>

            <div className="auth-inline-links">
              <button type="button" onClick={() => navigate('/reset-password')} className="auth-link-btn">
                비밀번호 찾기
              </button>
              <span className="auth-inline-sep" aria-hidden="true">·</span>
              <button
                type="button"
                onClick={() => navigate(`/signup?redirect_uri=${encodeURIComponent(redirectUri)}`)}
                className="auth-link-btn"
              >
                회원가입
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
